import { FC, memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';
import { MAX_DATE } from 'constants/common';
import { DATE_FORMAT_MMM_YYYY } from 'constants/formats';
import dayjs from 'dayjs';

import { AutocompleteControl } from 'components/_basic/Form/Autocomplete';
import { DatePickerControl } from 'components/_basic/Form/DatePicker';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { BorderedCard } from 'components/BorderedCard';
import { ISectionRowProps as IProps } from 'components/BusinessToolForm';
import { PRIORITY_OPTIONS_DEU, TERMS_OPTIONS_DEU } from 'components/BusinessToolForm/constants/deu/options_deu';
import { PRIORITY_OPTIONS, TERMS_OPTIONS } from 'components/BusinessToolForm/constants/options';
import { PRIORITY_OPTIONS_UA, TERMS_OPTIONS_UA } from 'components/BusinessToolForm/constants/ua/options_ua';
import { GOAL_ROW_NAMES } from 'components/BusinessToolForm/Sections/AboutCompany/MissionAndVision/utils/form';

import { concatWithDot } from 'helpers/string';

export const GoalAndObjectiveRow: FC<IProps> = memo(({ name, disabled = false }) => {
  const { t, i18n } = useTranslation();
  const { control } = useFormContext();

  const getChosenPriorityOptions = (lang: string) => {
    switch (lang) {
      case 'de':
        return PRIORITY_OPTIONS_DEU;

      case 'uk':
        return PRIORITY_OPTIONS_UA;

      default:
        return PRIORITY_OPTIONS;
    }
  };
  const getChosenTermsOptions = (lang: string) => {
    switch (lang) {
      case 'de':
        return TERMS_OPTIONS_DEU;

      case 'uk':
        return TERMS_OPTIONS_UA;

      default:
        return TERMS_OPTIONS;
    }
  };

  return (
    <BorderedCard>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextInputControl
            control={control}
            id={concatWithDot(name, GOAL_ROW_NAMES.GOAL)}
            name={concatWithDot(name, GOAL_ROW_NAMES.GOAL)}
            label={t('goal')}
            disabled={disabled}
            maxLength={150}
          />
        </Grid>
        <Grid item xs={4}>
          <AutocompleteControl
            control={control}
            id={concatWithDot(name, GOAL_ROW_NAMES.PRIORITY)}
            label={t('executiveSummary.goalsAndObjectives.goals.priority')}
            name={concatWithDot(name, GOAL_ROW_NAMES.PRIORITY)}
            options={getChosenPriorityOptions(i18n.language)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={4}>
          <AutocompleteControl
            control={control}
            id={concatWithDot(name, GOAL_ROW_NAMES.TERMS)}
            label={t('executiveSummary.goalsAndObjectives.goals.terms')}
            name={concatWithDot(name, GOAL_ROW_NAMES.TERMS)}
            options={getChosenTermsOptions(i18n.language)}
            defaultValue={getChosenTermsOptions(i18n.language)[0]}
            disabled={disabled}
            safeDefaultValueOnClear
          />
        </Grid>
        <Grid item xs={4}>
          <DatePickerControl
            control={control}
            id={concatWithDot(name, GOAL_ROW_NAMES.SCHEDULED_DATE)}
            name={concatWithDot(name, GOAL_ROW_NAMES.SCHEDULED_DATE)}
            label={t('executiveSummary.goalsAndObjectives.goals.scheduledDate')}
            disabled={disabled}
            views={['month', 'year']}
            maxDate={dayjs(MAX_DATE)}
            dateFormat={DATE_FORMAT_MMM_YYYY}
          />
        </Grid>
        <Grid item xs={12}>
          <TextInputControl
            multiline
            control={control}
            id={concatWithDot(name, GOAL_ROW_NAMES.MECHANISM_OF_ACHIEVEMENT)}
            label={t('executiveSummary.goalsAndObjectives.goals.mechanismOfAchievement')}
            name={concatWithDot(name, GOAL_ROW_NAMES.MECHANISM_OF_ACHIEVEMENT)}
            maxLength={500}
            minRows={4}
            disabled={disabled}
          />
        </Grid>
      </Grid>
    </BorderedCard>
  );
});
